import { useState } from "react";
import { Deck } from "@/lib/classes/deck";
import { UnitCategory } from "@/lib/types/deck-builder";
import type { DeckUnit } from "@/lib/types/deck-builder";
import type { Unit } from "@/lib/types/unit";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, AlertCircle, Settings, ChevronUp, Minus, Info } from "lucide-react";
import { getUnitImagePath } from "@/lib/utils/unit-images";
import { getTraitIconPath, filterTraits } from "@/lib/utils/trait-icons";
import { TransportSelection } from "./TransportSelection";
import { UnitInfoDialog } from "./UnitInfoDialog";
import { useComparisonModals } from "./ComparisonModalManager";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SlotCostGrid } from "@/components/SlotCostGrid";

interface DeckViewProps {
  deck: Deck;
  onRemoveUnit?: (unit: DeckUnit) => void;
  onUpdateUnit?: (oldUnit: DeckUnit, newUnit: DeckUnit) => void;
}

// Helper function to get category code from category enum
function getCategoryCode(category: UnitCategory): string {
  const codeMap: Record<UnitCategory, string> = {
    [UnitCategory.LOG]: "LOG",
    [UnitCategory.INF]: "INF",
    [UnitCategory.ART]: "ART",
    [UnitCategory.TNK]: "TNK",
    [UnitCategory.REC]: "REC",
    [UnitCategory.AA]: "AA",
    [UnitCategory.HEL]: "HEL",
    [UnitCategory.AIR]: "AIR",
    [UnitCategory.DEFENSE]: "DEF",
  };
  return codeMap[category] || category.split("/").pop() || "";
}

export function DeckView({ deck, onRemoveUnit, onUpdateUnit }: DeckViewProps) {
  const groupedUnits = deck.unitsInDeckGroupedUnitsByCategory;
  const totalPoints = deck.getSumOfUnitsCosts();
  const warningUpperThreshold = 14500;
  const warningLowerThreshold = 12000;
  const showWarning = totalPoints > warningUpperThreshold || totalPoints < warningLowerThreshold;

  // Calculate activation point values for progress bar
  const totalSpent = deck.usedActivationPoints;
  const maxPoints = deck.division.maxActivationPoints;
  const remaining = maxPoints - totalSpent;
  const percentage = (totalSpent / maxPoints) * 100;

  return (
    <div className="flex h-full min-w-0 flex-col">
      {/* Activation Points Progress Bar */}
      <div className="mb-4 px-1">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-muted-foreground">Activation Points</span>
          <span className="font-medium">
            {totalSpent} / {maxPoints}
          </span>
        </div>
        <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300"
            style={{ width: `${Math.min(percentage, 100)}%` }}
          />
        </div>
        <div className="text-xs text-muted-foreground mt-1">
          {remaining > 0 ? `${remaining} remaining` : "Full"}
        </div>
      </div>

      <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        <Accordion type="multiple" defaultValue={deck.unitCategories} className="w-full min-w-0">
          {deck.unitCategories.map((category) => {
            const units = groupedUnits[category] || [];
            const slotCosts = deck.slotCosts[category] || [];
            const usedSlots = units.length;
            const totalSlots = deck.getTotalSlotsForCategory(category);
            const unitCount = deck.getTotalUnitCountForCategory(category);
            const categoryPoints = deck.getSumOfUnitCostsForCategory(category);
            const nextSlotIndex = deck.getNextSlotCostIndexForCategory(category) || 0;
            const categoryAP = slotCosts.slice(0, usedSlots).reduce((sum, cost) => sum + cost, 0);

            return (
              <AccordionItem key={category} value={category} className="border-b last:border-b-0">
                <AccordionTrigger className="hover:no-underline py-3">
                  <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5 pr-2 text-left sm:pr-4">
                    <div className="flex items-center justify-between w-full">
                      <h4 className="text-sm font-semibold uppercase text-foreground tracking-wide">
                        {getCategoryCode(category)}
                      </h4>
                      <div className="text-xs font-medium text-muted-foreground">
                        {categoryAP} AP
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="font-medium">{usedSlots} / {totalSlots} slots</span>
                      <span>{unitCount} units</span>
                      <span>{categoryPoints} points</span>
                    </div>
                    <SlotCostGrid costs={slotCosts} filledSlotCount={nextSlotIndex} />
                  </div>
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid grid-cols-2 2xl:grid-cols-3 gap-1.5 sm:gap-2 justify-items-center pt-2">
                    {units.length > 0 ? (
                      units.map((deckUnit, index) => {
                        const unit = deck.getUnitForPack(deckUnit.pack);
                        if (!unit) return null;

                        return (
                          <DeckUnitCard
                            key={index}
                            unit={unit}
                            deckUnit={deckUnit}
                            deck={deck}
                            onRemoveUnit={onRemoveUnit}
                            onUpdateUnit={onUpdateUnit}
                          />
                        );
                      })
                    ) : (
                      <div className="text-sm text-muted-foreground text-center py-6 italic col-span-full">
                        No cards in category
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      <div className="border-t pt-3 mt-auto flex items-center justify-end gap-2 shrink-0">
        <span className="text-sm font-medium text-muted-foreground">Total Points:</span>
        <span className={`text-sm font-semibold ${showWarning ? "text-orange-500" : "text-foreground"}`}>
          {totalPoints.toLocaleString()}
        </span>
        {showWarning && (
          <div className="relative group">
            <AlertCircle className="h-4 w-4 text-orange-500 cursor-help" />
            <div className="absolute bottom-full right-0 mb-2 w-56 p-2 bg-popover border rounded-md shadow-lg text-xs text-popover-foreground opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10 whitespace-normal">
              {totalPoints > warningUpperThreshold
                ? "Typical decks should be under 14,500 points"
                : "Typical decks should be more than 12,000 points"}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface DeckUnitCardProps {
  unit: Unit;
  deckUnit: DeckUnit;
  deck: Deck;
  onRemoveUnit?: (unit: DeckUnit) => void;
  onUpdateUnit?: (oldUnit: DeckUnit, newUnit: DeckUnit) => void;
}

function DeckUnitCard({ unit, deckUnit, deck, onRemoveUnit, onUpdateUnit }: DeckUnitCardProps) {
  const [imageError, setImageError] = useState(false);
  const [transportDialogOpen, setTransportDialogOpen] = useState(false);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [traitImageErrors, setTraitImageErrors] = useState<Set<number>>(new Set());
  const { openComparisonModal } = useComparisonModals();
  
  // Calculate costs
  const quantity = deck.getUnitQuantityForPack(deckUnit.pack, deckUnit.veterancy);
  const unitCost = unit.commandPoints || 0;
  const transportCost = deckUnit.transport?.commandPoints || 0;
  
  // Get veterancy quantities
  const veterancyQuantities = Deck.getVeterancyQuantitiesForPack(deckUnit.pack, unit);
  
  // Filter and get traits (specialities starting with "_")
  const allTraits = filterTraits(unit.specialities || []);
  const traitIcons = allTraits;
  
  const handleTransportChange = (newTransport: Unit | null) => {
    if (onUpdateUnit) {
      const updatedUnit: DeckUnit = {
        ...deckUnit,
        transport: newTransport || undefined,
      };
      onUpdateUnit(deckUnit, updatedUnit);
    }
    setTransportDialogOpen(false);
  };
  
  const handleVeterancyChange = (newVeterancy: number) => {
    const quantity = veterancyQuantities[newVeterancy];
    if (quantity !== undefined && quantity > 0 && onUpdateUnit) {
      const updatedUnit: DeckUnit = {
        ...deckUnit,
        veterancy: newVeterancy,
      };
      onUpdateUnit(deckUnit, updatedUnit);
    }
  };
  
  const transports = deck.getTransportsForPack(deckUnit.pack);
  const canUseWithoutTransport = deck.getPackAvailableWithoutTransport(deckUnit.pack);
  const availableTransports = transports || [];
  
  return (
    <>
      <Card key={deckUnit.pack.packDescriptor} className="overflow-hidden w-full h-full">
        <CardContent className="p-0 h-full">
          <div className="flex flex-col h-full">
            {/* Trait Icons Header */}
            <div className="flex items-center justify-center gap-0.5 sm:gap-1 px-1 sm:px-2 h-[20px] sm:h-[24px] lg:h-[28px] bg-muted/50">
              {traitIcons.length > 0 ? (
                <>
                  {traitIcons.map((trait, index) => {
                    const iconPath = getTraitIconPath(trait);
                    const hasError = traitImageErrors.has(index);
                    
                    return (
                      <div
                        key={index}
                        className="w-4 h-4 sm:w-[18px] sm:h-[18px] lg:w-5 lg:h-5 rounded bg-pink-500/20 border border-pink-500/40 flex items-center justify-center overflow-hidden"
                        title={trait}
                      >
                        {iconPath && !hasError ? (
                          <img
                            src={iconPath}
                            alt={trait}
                            className="w-full h-full object-contain"
                            style={{
                              filter: 'var(--trait-icon-filter)'
                            }}
                            onError={() => {
                              setTraitImageErrors(prev => new Set(prev).add(index));
                            }}
                          />
                        ) : (
                          <span className="text-[8px] sm:text-[9px] lg:text-[10px] text-pink-500 font-bold">
                            {trait.replace('_', '').charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </>
              ) : (
                <span className="text-xs text-muted-foreground">No Traits</span>
              )}
            </div>

            {/* Main Image Area with Overlays */}
            <div className="relative w-full min-h-[50px] sm:min-h-[60px] lg:min-h-[76px] bg-muted flex items-center justify-center">
              {!imageError ? (
                <img
                  src={getUnitImagePath(unit.descriptorName)}
                  alt={unit.name}
                  className="w-full h-auto"
                  onError={() => setImageError(true)}
                />
              ) : (
                <div className="w-full min-h-[50px] sm:min-h-[60px] lg:min-h-[76px] flex items-center justify-center bg-muted">
                  <span className="text-[10px] sm:text-xs text-muted-foreground">No Image</span>
                </div>
              )}

              {/* Overlay Remove Button (top-left) */}
              {onRemoveUnit && (
                <button
                  onClick={() => onRemoveUnit(deckUnit)}
                  className="absolute top-[1px] sm:top-[2px] left-[1px] sm:left-[2px] w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 bg-pink-500 hover:bg-pink-600 rounded flex items-center justify-center shadow-lg transition-colors z-10"
                  aria-label="Remove unit"
                >
                  <Minus className="h-3 w-3 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4 text-white" />
                </button>
              )}

              {/* Overlay Info Button (top-right) */}
              <button
                onClick={() => {
                  // On desktop, open comparison modal directly; on mobile, use regular modal
                  if (typeof window !== "undefined" && window.innerWidth >= 1024) {
                    openComparisonModal(unit);
                  } else {
                    setInfoDialogOpen(true);
                  }
                }}
                className="absolute top-[1px] sm:top-[2px] right-[1px] sm:right-[2px] w-5 h-5 sm:w-6 sm:h-6 lg:w-7 lg:h-7 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center shadow-lg transition-colors z-10"
                aria-label="Unit information"
              >
                <Info className="h-2.5 w-2.5 sm:h-3 sm:w-3 lg:h-3.5 lg:w-3.5 text-white" />
              </button>

              {/* Quantity Indicator (bottom-left) */}
              <div className="absolute bottom-[1px] sm:bottom-[2px] left-[1px] sm:left-[2px] bg-black/70 px-1 sm:px-1.5 py-0.5 rounded text-white text-[10px] sm:text-xs font-semibold">
                x{quantity}
              </div>

              {/* Cost Indicator (bottom-right) - Total cost (unit + transport) */}
              <div className="absolute bottom-[1px] sm:bottom-[2px] right-[1px] sm:right-[2px] bg-black/70 px-1 sm:px-1.5 py-0.5 rounded text-white text-[10px] sm:text-xs font-semibold">
                {unitCost + transportCost}
              </div>
            </div>

            {/* Unit Identifier Text */}
            <div className="px-1 sm:px-1.5 lg:px-2 py-1 sm:py-1.5 bg-card border-t">
              <div className="flex items-center justify-between gap-1 sm:gap-2">
                <div className="text-[10px] sm:text-xs font-medium truncate flex-1 leading-tight">
                  {unit.name}
                </div>
                <span className="text-[10px] sm:text-xs text-muted-foreground">{unitCost}</span>
              </div>
              {/* Transport Info */}
              {deckUnit.transport && (
                <div className="flex items-center justify-between gap-1 sm:gap-2 mt-0.5">
                  <div className="flex items-center gap-0.5 sm:gap-1 flex-1 min-w-0">
                    <div className="text-[10px] sm:text-xs text-muted-foreground truncate leading-tight">
                      {deckUnit.transport.name}
                    </div>
                    {availableTransports.length > 0 && onUpdateUnit && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTransportDialogOpen(true)}
                        className="h-3.5 w-3.5 sm:h-4 sm:w-4 p-0 flex-shrink-0"
                        title="Change transport"
                      >
                        <Settings className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                      </Button>
                    )}
                  </div>
                  <span className="text-[10px] sm:text-xs text-muted-foreground">{transportCost}</span>
                </div>
              )}
            </div>

            {/* Veterancy Selection Buttons */}
            <div className="px-1 sm:px-1.5 lg:px-2 pb-0.5 sm:pb-1 pt-1 sm:pt-1.5 flex items-center justify-center gap-0.5 sm:gap-1 mt-auto">
              {veterancyQuantities.map((qty, index) => {
                const isSelected = deckUnit.veterancy === index;
                const isDisabled = qty === 0 || !onUpdateUnit;
                
                // Determine icon based on veterancy level
                let icon;
                if (index === 0) {
                  icon = <Minus className="h-3 w-3 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4 stroke-[2.5]" />;
                } else if (index === 1) {
                  icon = <ChevronUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 lg:h-4 lg:w-4 stroke-[2.5]" />;
                } else if (index === 2) {
                  icon = (
                    <div className="flex flex-col items-center justify-center -space-y-0.5" style={{ width: '12px', minWidth: '12px' }}>
                      <ChevronUp className="h-2.5 sm:h-2.5 lg:h-3 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                      <ChevronUp className="h-2.5 sm:h-2.5 lg:h-3 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                    </div>
                  );
                } else if (index === 3) {
                  icon = (
                    <div className="flex flex-col items-center justify-center -space-y-0.5" style={{ width: '12px', minWidth: '12px' }}>
                      <ChevronUp className="h-2 sm:h-2.5 lg:h-2.5 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                      <ChevronUp className="h-2 sm:h-2.5 lg:h-2.5 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                      <ChevronUp className="h-2 sm:h-2.5 lg:h-2.5 stroke-[2.5]" style={{ width: '12px', minWidth: '12px' }} />
                    </div>
                  );
                } else {
                  icon = <span className="text-[8px] sm:text-[9px] lg:text-[10px] font-bold">{index}</span>;
                }

                return (
                  <button
                    key={index}
                    onClick={() => onUpdateUnit && handleVeterancyChange(index)}
                    disabled={isDisabled}
                    className={`
                      relative flex-1 max-w-[36px] sm:max-w-[40px] lg:max-w-[48px] h-6 sm:h-7 lg:h-8 flex items-center justify-center
                      border-2 rounded-md transition-colors
                      ${isSelected 
                        ? 'border-pink-500 bg-pink-500/20' 
                        : 'border-pink-500/40 bg-pink-500/10'
                      }
                      ${isDisabled 
                        ? 'opacity-30 cursor-not-allowed' 
                        : 'cursor-pointer hover:bg-pink-500/20'
                      }
                    `}
                    title={`Veterancy ${index}: ${qty} units`}
                  >
                    <div className="text-foreground w-3 sm:w-3.5 lg:w-4 flex items-center justify-center">
                      {icon}
                    </div>
                    {qty > 0 && (
                      <span className="absolute -bottom-0.5 sm:-bottom-1 -right-0.5 sm:-right-1 text-[7px] sm:text-[8px] text-pink-500 font-bold bg-card rounded-full w-3 h-3 sm:w-3.5 sm:h-3.5 flex items-center justify-center border border-pink-500/40">
                        {qty}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {availableTransports.length > 0 && (
        <TransportSelection
          open={transportDialogOpen}
          onClose={() => setTransportDialogOpen(false)}
          transports={availableTransports}
          onSelect={handleTransportChange}
          allowNone={canUseWithoutTransport}
        />
      )}
      
      <UnitInfoDialog
        open={infoDialogOpen}
        onOpenChange={setInfoDialogOpen}
        unit={unit}
      />
    </>
  );
}

